import { type OlkTimeAndPlacesErrorCodes } from 'owa-error-codes';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { CustomWaterfallRange } from 'owa-analytics-types';
import { MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import type { IUTEPlacesFeatureLatencyArgs } from '@outlook/unified_telemetry';
import {
    type UTPageType,
    type UTPlacesFeatureAction,
    type UTPlacesFeatureName,
    type UTPlacesFeatureUIContext,
} from '@outlook/unified_telemetry';
import { AdminRoles, getPermissionMap } from 'places-management-permission';
import React from 'react';

const roleSwitcherLoadPerfCheckpoints: string[] = [
    'CheckPermissionsCompleted',
    'CheckPermissionsException',
    'Loaded',
    'WontLoad',
];
type RoleSwitcherLoadPerfCheckpoint = typeof roleSwitcherLoadPerfCheckpoints[number];

export function markRoleSwitcherPerfCheckpoint(
    datapoint: PerformanceDatapoint,
    checkpoint: RoleSwitcherLoadPerfCheckpoint
): void {
    const waterfallIndex = getCheckpointIndex(checkpoint);
    if (!datapoint.hasEnded && !datapoint.isWaterfallCheckpointDefined(waterfallIndex)) {
        datapoint.addToCustomWaterfall(waterfallIndex, checkpoint);
    }
}

export function useRoleSwitcherLoadE2E(
    defaultRoute: string,
    routeToPageMap: Map<string, UTPageType>,
    shouldShowRoleSwitcher: boolean,
    isCheckingPermissions: boolean,
    checkPermissionFailed: boolean = false
): {
    roleSwitcherRef: React.RefObject<HTMLButtonElement | null>;
} {
    const roleSwitcherRef = React.useRef<HTMLButtonElement>(null);
    const roleSwitcherLoadDatapointRef = React.useRef<PerformanceDatapoint | null>(null);
    const defaultRouteRef = React.useRef(defaultRoute);

    const addUTtoDatapoint = React.useCallback(
        (dp: PerformanceDatapoint) => {
            const permissionMap = getPermissionMap();
            dp.addUnifiedTelemetryData({
                eventName: 'PlacesFeatureLatency',
                data: {
                    PageType: routeToPageMap.get(defaultRouteRef.current) || 'HomeDashboard',
                    FeatureName: 'PageLoad',
                    FeatureAction: 'Load',
                    FeatureUIContext: 'RoleSwitcher',
                    PlaceManagementData: {
                        PlaceAdminRoles: {
                            isBuildingAdmin:
                                permissionMap.get(AdminRoles.placesBuildingManagement) || false,
                            isTenantAdmin:
                                permissionMap.get(AdminRoles.tenantPlacesManagement) || false,
                            isDeskAdmin:
                                permissionMap.get(AdminRoles.PlacesDeskManagement) || false,
                        },
                    },
                } as IUTEPlacesFeatureLatencyArgs,
            });
        },
        [routeToPageMap]
    );

    // Initialize the PerformanceDatapoint for role switcher load
    React.useEffect(() => {
        if (!roleSwitcherLoadDatapointRef.current) {
            const mailboxInfo = getModuleContextMailboxInfo();
            roleSwitcherLoadDatapointRef.current = new PerformanceDatapoint(
                'PlacesFeatureLatency',
                mailboxInfo,
                {
                    timeout: MILLISECONDS_IN_MINUTE * 3,
                }
            );
            roleSwitcherLoadDatapointRef.current.addCustomData({
                scenario: 'PlacesRoleSwitcherLoadE2E',
            });
        }

        return () => {
            if (
                roleSwitcherLoadDatapointRef?.current &&
                !roleSwitcherLoadDatapointRef.current.hasEnded
            ) {
                // end the data point if user navigates away from the page
                addUTtoDatapoint(roleSwitcherLoadDatapointRef.current);
                roleSwitcherLoadDatapointRef.current.endWithError(
                    DatapointStatus.UserCancelled,
                    '39_0101'
                );
                roleSwitcherLoadDatapointRef.current = null;
            }
        };
    }, [addUTtoDatapoint]);

    // mark the end of the role switcher load datapoint
    React.useEffect(() => {
        const dp = roleSwitcherLoadDatapointRef.current;
        if (shouldShowRoleSwitcher && dp && !dp.hasEnded) {
            // add settimeout to make sure the check permission checkpoint is added before the end of the datapoint
            setTimeout(() => {
                addUTtoDatapoint(dp);
                markRoleSwitcherPerfCheckpoint(dp, 'Loaded');
                dp.end();
            }, 0);
        }
    }, [addUTtoDatapoint, shouldShowRoleSwitcher]);

    // mark the role switcher load datapoint as wont load if the role switcher is not rendered
    const markWontLoad = React.useCallback(() => {
        const dp = roleSwitcherLoadDatapointRef.current;
        if (dp && !dp.hasEnded && roleSwitcherRef.current == null) {
            addUTtoDatapoint(dp);
            markRoleSwitcherPerfCheckpoint(dp, 'WontLoad');
            dp.end();
        }
    }, [addUTtoDatapoint]);

    // If check permission complete or throw an exception, mark the role switcher load checkpoint
    const prevCheckingPermissionRef = React.useRef(isCheckingPermissions);
    React.useEffect(() => {
        const dp = roleSwitcherLoadDatapointRef.current;
        if (prevCheckingPermissionRef.current && !isCheckingPermissions && dp && !dp.hasEnded) {
            if (checkPermissionFailed) {
                markRoleSwitcherPerfCheckpoint(dp, 'CheckPermissionsException');
            } else {
                markRoleSwitcherPerfCheckpoint(dp, 'CheckPermissionsCompleted');
            }

            // add settimeout to make sure the won't load checkpoint really happens after the decision to not load the role switcher
            setTimeout(() => {
                markWontLoad();
            }, 100);
        }
        prevCheckingPermissionRef.current = isCheckingPermissions;
    }, [checkPermissionFailed, isCheckingPermissions, markWontLoad]);

    return {
        roleSwitcherRef,
    };
}

/**
 * This function receives a number and narrows its type to CustomWaterfallRange if it is in the range
 * @param index the number to check
 * @returns true if the number is in the CustomWaterfallRange. Narrows down the type to CustomWaterfallRange
 */
function isInCustomWaterfallRange(index: number): index is CustomWaterfallRange {
    // PerformanceDatapoint class can only track a fixed number of custom waterfall checkpoints
    // specifically 15 different timing checkpoints within a single performance measurement flow
    return Number.isInteger(index) && index > 0 && index <= 15;
}

/**
 * Gets the checkpoint index in the CustomWaterfallRange type
 * @param checkpoint the checkpoint name
 * @returns a number between 1 and 15 with the type needed to satisfy addToCustomWaterfall
 */
function getCheckpointIndex(checkpoint: RoleSwitcherLoadPerfCheckpoint): CustomWaterfallRange {
    const arrayIndex = roleSwitcherLoadPerfCheckpoints.indexOf(checkpoint);
    const checkpointIndex = arrayIndex + 1; // CustomWaterfallRange starts from 1
    if (!isInCustomWaterfallRange(checkpointIndex)) {
        throw new Error('checkpoint index exceeds the CustomWaterfallRange');
    }
    return checkpointIndex;
}
